import React, { createContext, ReactNode } from 'react';
import { MaterialDesignContent, enqueueSnackbar, SnackbarProvider } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircleOutline, ErrorOutlineOutlined } from '@mui/icons-material';
import { styled } from '@mui/material';
import { NOTIFICATION_ALERT_TIMEOUT } from 'shared';
type NotificationTypes = 'success' | 'error' | 'warning' | 'info';

type NotificationContextType = {
  setNotification: (message: string, type: NotificationTypes) => void;
};

const initialState: NotificationContextType = {
  setNotification: () => [],
};

const StyledMaterialDesignContent = styled(MaterialDesignContent)(({ theme }) => ({
  '&.notistack-MuiContent': {
    marginRight: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
  },
  '&.notistack-MuiContent-success': {
    backgroundColor: theme.palette.success.dark,
    color: theme.palette.success.light,
    boxShadow: 'none',
    [`& .MuiSvgIcon-root`]: {
      marginRight: theme.spacing(1.5),
    },
  },
  '&.notistack-MuiContent-error': {
    backgroundColor: theme.palette.error.dark,
    color: theme.palette.error.contrastText,
    boxShadow: 'none',
    [`& .MuiSvgIcon-root`]: {
      marginRight: theme.spacing(1.5),
    },
  },
}));

export const NotificationContext = createContext(initialState);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();

  queryClient.setDefaultOptions({
    ...queryClient.getDefaultOptions(),
    mutations: {
      onError: (data) => {
        // @ts-ignore
        const responseMessage = data?.response?.data?.message;
        if (responseMessage) {
          setNotification(responseMessage, 'error');
        }
      },
      onSuccess: (data) => {
        // @ts-ignore
        const responseMessage = data?.message;
        if (responseMessage) {
          setNotification(responseMessage, 'success');
        }
      },
    },
  });

  const setNotification = (message: string, type: NotificationTypes) => {
    enqueueSnackbar(message, { key: Math.random(), variant: type });
  };

  const value: NotificationContextType = { setNotification };

  return (
    <NotificationContext.Provider value={value}>
      <SnackbarProvider
        autoHideDuration={NOTIFICATION_ALERT_TIMEOUT}
        iconVariant={{
          success: <CheckCircleOutline />,
          error: <ErrorOutlineOutlined />,
        }}
        Components={{
          success: StyledMaterialDesignContent,
          error: StyledMaterialDesignContent,
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        maxSnack={5}
      >
        {children}
      </SnackbarProvider>
    </NotificationContext.Provider>
  );
};
